import { Handle, Position, NodeResizer } from 'reactflow';
import { Database, FileText, Table } from 'lucide-react';

interface DestinationNodeProps {
    data: any;
    selected: boolean;
}

export default function DestinationNode({ data, selected }: DestinationNodeProps) {
    const isRunning = data?.executionStatus === 'Running';
    const destType = data.destinationType || 'Table';

    const Icon = destType === 'File' ? FileText : destType === 'Dataset' ? Table : Database;
    const colorClass = destType === 'File' ? 'text-green-600' : destType === 'Dataset' ? 'text-blue-600' : 'text-indigo-600';
    const borderClass = destType === 'File' ? 'border-green-400 bg-green-50' : destType === 'Dataset' ? 'border-blue-400 bg-blue-50' : 'border-indigo-400 bg-indigo-50';

    return (
        <div className="relative flex flex-col items-center">
            <NodeResizer
                color="#94a3b8"
                isVisible={selected}
                minWidth={48}
                minHeight={48}
                handleStyle={{ width: 8, height: 8, borderRadius: 2 }}
            />

            {/* Main Icon Container - Compact & Premium */}
            <div
                className={`
                    relative group flex items-center justify-center
                    w-[56px] h-[56px] rounded-2xl shadow-sm border-[1.5px] transition-all duration-300 z-10 bg-white
                    ${selected ? 'border-yellow-400 ring-4 ring-yellow-400/10 shadow-xl scale-105' : `${borderClass.replace('bg-', 'border-')} hover:shadow-md hover:-translate-y-0.5`}
                `}
            >
                {/* Status Indicator (Top Right) */}
                {isRunning && (
                    <div className="absolute top-0 right-0 p-1">
                        <span className="relative flex h-2 w-2">
                            <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${colorClass.replace('text-', 'bg-')}`}></span>
                            <span className={`relative inline-flex rounded-full h-2 w-2 ${colorClass.replace('text-', 'bg-')}`}></span>
                        </span>
                    </div>
                )}

                <Icon className={`h-7 w-7 transition-all duration-300 group-hover:scale-110 ${colorClass}`} strokeWidth={1.5} />

                {/* Input Handle */}
                <Handle
                    type="target"
                    position={Position.Left}
                    id="in"
                    className="!w-3 !h-3 !min-w-0 !min-h-0 !bg-transparent !border-0 flex items-center justify-center z-50 cursor-pointer"
                    style={{ left: -12 }}
                >
                    <div className={`w-1.5 h-1.5 border-t-[2px] border-r-[2px] rotate-45 transform -translate-x-0.5 border-slate-400 group-hover:border-slate-600 transition-colors`} />
                </Handle>
            </div>

            {/* Floating Label (Below) */}
            <div className="mt-1.5 flex flex-col items-center space-y-0.5">
                <span className="text-[10px] font-bold text-slate-600 uppercase tracking-wide leading-none select-none">
                    {data.label || 'Destination'}
                </span>

                {/* Subtle Metadata */}
                {(data.tableName || data.fileName || data.datasetName) && (
                    <div className="text-[8px] text-slate-400 font-medium max-w-[100px] truncate">
                        {data.tableName || data.fileName || data.datasetName}
                    </div>
                )}
            </div>
        </div>
    );
}